import { Box, CircularProgress, Typography } from "@mui/material";
import { fmtDias } from "../utils/format";
import { TE_CARD_SX, TE_COLORS } from "./theme";

interface KpiCardsProps {
  kpis: {
    hasConcluded: boolean;
    concludedCount: number;
    tempoMedioTotal: number;
    mediana: number;
    slowest: { etapa: string; etapaValue?: string; media: number } | null;
    acimaMedia: number;
    pctAcimaMedia: number;
    presoAgora: { etapa: string; count: number; media: number } | null;
    comReabertura: number;
    maxReaberturas: number;
    pctComReabertura: number;
    totalNoFiltroReab: number;
  };
  drillType: string | null;
  onEtapaLenta: () => void;
  onAcimaMedia: () => void;
  onEtapaParada: () => void;
  onReabertura: () => void;
  /** Cada card espera um dado diferente -- mostra loading individual em
   * vez de travar o dashboard inteiro esperando o mais lento. */
  kpisLoading?: boolean;
  presoAgoraLoading?: boolean;
  reaberturaLoading?: boolean;
}

function Card({
  label,
  letter,
  letterBg,
  letterFg,
  value,
  valueColor,
  sub,
  clickable,
  active,
  onClick,
  loading,
}: {
  label: string;
  letter: string;
  letterBg: string;
  letterFg: string;
  value: string;
  valueColor?: string;
  sub: string;
  clickable?: boolean;
  active?: boolean;
  onClick?: () => void;
  loading?: boolean;
}) {
  return (
    <Box
      onClick={clickable ? onClick : undefined}
      sx={{
        ...TE_CARD_SX,
        cursor: clickable ? "pointer" : "default",
        transition: "box-shadow .15s, border-color .15s",
        ...(active
          ? {
              borderColor: TE_COLORS.amberFg,
              boxShadow: `0 0 0 2px #F7E2AB inset`,
            }
          : {}),
        "&:hover": clickable
          ? { boxShadow: "0 2px 10px rgba(0,0,0,.06)", borderColor: "#E0C98A" }
          : undefined,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography
          sx={{
            fontSize: 11,
            color: TE_COLORS.textFaint,
            textTransform: "uppercase",
            letterSpacing: "0.4px",
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "9px",
            bgcolor: letterBg,
            color: letterFg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {letter}
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, mb: 0.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              mt: 1.25,
              color: valueColor ?? TE_COLORS.text,
            }}
          >
            {value}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TE_COLORS.textDim, mt: 0.5 }}>
            {sub}
          </Typography>
        </>
      )}
    </Box>
  );
}

export function KpiCards({
  kpis,
  drillType,
  onEtapaLenta,
  onAcimaMedia,
  onEtapaParada,
  onReabertura,
  kpisLoading,
  presoAgoraLoading,
  reaberturaLoading,
}: KpiCardsProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: 1.5,
        mb: 2.5,
      }}
    >
      {!kpis.hasConcluded && !kpisLoading ? (
        <>
          <Card
            label="Tempo médio total"
            letter="T"
            letterBg={TE_COLORS.blueBg}
            letterFg={TE_COLORS.blue}
            value="–"
            sub="0 projeto(s) concluído(s) no filtro"
          />
          <Card
            label="Mediana do ciclo"
            letter="M"
            letterBg={TE_COLORS.purpleBg}
            letterFg={TE_COLORS.purple}
            value="–"
            sub="sem dados"
          />
          <Card
            label="Etapa mais demorada"
            letter="E"
            letterBg={TE_COLORS.orangeBg}
            letterFg={TE_COLORS.orange}
            value="–"
            sub="sem dados (concluídos)"
          />
          <Card
            label="Projetos acima da média"
            letter="P"
            letterBg={TE_COLORS.redBg}
            letterFg={TE_COLORS.red}
            value="–"
            sub="sem dados"
          />
        </>
      ) : (
        <>
          <Card
            label="Tempo médio total"
            letter="T"
            letterBg={TE_COLORS.blueBg}
            letterFg={TE_COLORS.blue}
            value={fmtDias(kpis.tempoMedioTotal)}
            valueColor={TE_COLORS.blue}
            sub={`${kpis.concludedCount} projeto(s) no filtro`}
            loading={kpisLoading}
          />
          <Card
            label="Mediana do ciclo"
            letter="M"
            letterBg={TE_COLORS.purpleBg}
            letterFg={TE_COLORS.purple}
            value={fmtDias(kpis.mediana)}
            valueColor={TE_COLORS.purple}
            sub="metade dos projetos abaixo disso"
            loading={kpisLoading}
          />
          <Card
            label="Etapa mais demorada"
            letter="E"
            letterBg={TE_COLORS.orangeBg}
            letterFg={TE_COLORS.orange}
            value={kpis.slowest?.etapa ?? "–"}
            valueColor={TE_COLORS.orange}
            sub={`${fmtDias(kpis.slowest?.media)} em média (concluídos)`}
            clickable={!!kpis.slowest}
            active={drillType === "etapaLenta"}
            onClick={onEtapaLenta}
            loading={kpisLoading}
          />
          <Card
            label="Projetos acima da média"
            letter="P"
            letterBg={TE_COLORS.redBg}
            letterFg={TE_COLORS.red}
            value={`${kpis.acimaMedia} projetos`}
            valueColor={TE_COLORS.red}
            sub={`${kpis.pctAcimaMedia}% do total analisado`}
            loading={kpisLoading}
            clickable
            active={drillType === "acimaMedia"}
            onClick={onAcimaMedia}
          />
        </>
      )}

      <Card
        label="Etapa com mais projetos parados agora"
        letter="S"
        letterBg={TE_COLORS.redBg}
        letterFg={TE_COLORS.red}
        value={kpis.presoAgora?.etapa ?? "–"}
        valueColor={TE_COLORS.red}
        sub={
          kpis.presoAgora
            ? `${kpis.presoAgora.count} projeto(s) · ${fmtDias(
                kpis.presoAgora.media
              )} em média`
            : "nenhum projeto em andamento no filtro"
        }
        clickable={!!kpis.presoAgora}
        active={drillType === "etapaParada"}
        onClick={onEtapaParada}
        loading={presoAgoraLoading}
      />

      <Card
        label="Projetos com reabertura"
        letter="R"
        letterBg={TE_COLORS.amberBg}
        letterFg={TE_COLORS.amberFg}
        value={`${kpis.comReabertura} projeto(s)`}
        valueColor={TE_COLORS.amberFg}
        sub={
          kpis.totalNoFiltroReab
            ? `${kpis.pctComReabertura}% do total · máx. ${kpis.maxReaberturas}x no mesmo projeto`
            : "sem dados no filtro"
        }
        clickable
        active={drillType === "reabertura"}
        loading={reaberturaLoading}
        onClick={onReabertura}
      />
    </Box>
  );
}
